import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Typed wrapper around `aiDraftPitch` (Sprint 79). Agent-pwa's first
 * AI surface — drafts a status-aware WhatsApp pitch the agent sends
 * to one of their operators.
 */

export const AiDraftPitchInputSchema = z.object({
  agentId: z.string().min(1),
  operatorName: z.string().min(1).max(120),
  status: z.enum(['active', 'trial', 'past_due', 'suspended', 'archived']),
  daysSinceSignup: z.number().int().nonnegative().optional(),
  daysSinceLastPaid: z.number().int().nonnegative().optional(),
  agentNote: z.string().max(200).optional(),
});
export type AiDraftPitchInput = z.infer<typeof AiDraftPitchInputSchema>;

export const AiDraftPitchOutputSchema = z.object({
  text: z.string(),
  cacheHit: z.boolean(),
  provider: z.string(),
});
export type AiDraftPitchOutput = z.infer<typeof AiDraftPitchOutputSchema>;

const aiDraftPitchFn: HttpsCallable<AiDraftPitchInput, AiDraftPitchOutput> = httpsCallable(
  functions,
  'aiDraftPitch',
);

export async function callAiDraftPitch(input: AiDraftPitchInput): Promise<AiDraftPitchOutput> {
  const parsed = AiDraftPitchInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('aiDraftPitch.input', parsed.error.flatten());
  }
  const result = await aiDraftPitchFn(parsed.data);
  const out = AiDraftPitchOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('aiDraftPitch.output', out.error.flatten());
  }
  return out.data;
}

/**
 * Typed wrapper around `aiAgentPriorityInsight` — Slice 1 of the AI-tools
 * "agent next-operator ranking". The rule-based order is computed client-side
 * (`rankOperators`); this thin call turns the top few into a "focus on these
 * first, and why" narrative. Privacy: only operator business names + lifecycle
 * status + coarse day-counts are sent — never customer PII. No key ⇒
 * `{ narrative: null, provider: 'noop' }` and the caller renders nothing.
 */
export const AiPriorityInsightItemSchema = z
  .object({
    operatorName: z.string().min(1).max(120),
    status: z.enum(['active', 'trial', 'past_due', 'suspended', 'archived']),
    daysSinceSignup: z.number().int().nonnegative().max(100_000).optional(),
    daysSinceLastPaid: z.number().int().nonnegative().max(100_000).optional(),
  })
  // Mirror the server schema's `.strict()` + caps so the "no customer PII to the
  // LLM" contract is symmetric on both sides (server remains the real gate).
  .strict();
export type AiPriorityInsightItem = z.infer<typeof AiPriorityInsightItemSchema>;

export const AiAgentPriorityInsightInputSchema = z.object({
  agentId: z.string().min(1),
  top: z.array(AiPriorityInsightItemSchema).min(1).max(5),
});
export type AiAgentPriorityInsightInput = z.infer<typeof AiAgentPriorityInsightInputSchema>;

export const AiAgentPriorityInsightOutputSchema = z.object({
  narrative: z.string().nullable(),
  provider: z.string(),
});
export type AiAgentPriorityInsightOutput = z.infer<typeof AiAgentPriorityInsightOutputSchema>;

const aiAgentPriorityInsightFn: HttpsCallable<
  AiAgentPriorityInsightInput,
  AiAgentPriorityInsightOutput
> = httpsCallable(functions, 'aiAgentPriorityInsight');

export async function callAiAgentPriorityInsight(
  input: AiAgentPriorityInsightInput,
): Promise<AiAgentPriorityInsightOutput> {
  const parsed = AiAgentPriorityInsightInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('aiAgentPriorityInsight.input', parsed.error.flatten());
  }
  const result = await aiAgentPriorityInsightFn(parsed.data);
  const out = AiAgentPriorityInsightOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('aiAgentPriorityInsight.output', out.error.flatten());
  }
  return out.data;
}
